import { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
//import { toast } from "react-toastify";

import {
  Box,
  Alert,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControl,
  // FormControlLabel,
  InputAdornment,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  // Switch,
  Slide,
  Typography,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import styled from "styled-components";

import SearchIcon from "@mui/icons-material/Search"; // MUI의 검색 아이콘
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
// import EditIcon from "@mui/icons-material/Edit";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { useProjectApi } from "../../../hooks/useProjectApi";
import {
  useGetTableData,
  useGetTableDataInQueryString,
  useCreateTableData,
  usePutTableData,
  useDeleteTableData,
} from "../../../components/Table/hooks";

import {
  registerCauseColumns,
  registerCauseColumns_edit,
  registerCauseColumns_delete,
  // registerCauseColumns_admin,
  standardSituationColumns,
} from "../../../constants/columns";
import {
  BreadcrumbsTable,
  ConfirmDialog,
  ConfirmDialogSingle,
  deepCopy,
  normalizeToArray,
  SnackbarAlert,
  formatDateWithDayjs,
  formatDateToDayjs,
} from "../../../components/common/HelperFunc";
import { AutoPilotGuide } from "../../../components/common/AutoPilotGuide";
import * as message from "../../../constants/messages";
import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import Buttons from "../../../../common/Buttons";
import { FONT_PROPS } from "../../../styles/colors";

//////////////////////////////////
// 스타일
//////////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 1199px) {
    padding: 12px;
    overflow: auto;
    overflow-x: hidden;
  }
`;

// 커스텀 MenuItem 스타일
const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  fontFamily: `${FONT_PROPS.PRIMARY} !important`,
  lineHeight: `${FONT_PROPS} !important`,
  fontWeight: `${FONT_PROPS.MEDIUM} !important`, // 폰트 굵기 medium
  color: `${FONT_PROPS.DARK} !important`, // 기본 텍스트 검정
  backgroundColor: "#ffffff", // 기본 배경 흰색
  "&:hover": {
    backgroundColor: "lightgray", // 마우스 올릴 때 연한 회색
  },
  "&.Mui-selected": {
    backgroundColor: "white !important", // 선택된 항목도 흰색 유지
    color: "black",
  },
  "&.Mui-selected:hover": {
    backgroundColor: "lightgray !important", // 선택된 상태에서 호버 시 연한 회색 유지
  },
}));

const SOURCE_FILTERS = [
  { value: "all", label: "전체" },
  { value: "worker", label: "근로자 제안" },
  { value: "manual", label: "일반 등록" },
];

const REGISTER_ASSET_FILTERS = [
  { value: "all", label: "전체" },
  { value: "connected", label: "유해·위험요인 연결" },
  { value: "empty", label: "미연결" },
];

const normalizeDateText = (value) => {
  if (!value) return "";
  return String(value).replace("T", " ").slice(0, 16);
};

const buildWorkerProposalSourceMap = (proposals = []) => {
  const map = new Map();
  const approvedProposals = (proposals || [])
    .filter((proposal) => proposal.status === "approved" && proposal.approved_danger_cause_id)
    .sort((a, b) => {
      const aDate = String(a.reviewed_at || a.created_at || "");
      const bDate = String(b.reviewed_at || b.created_at || "");
      if (aDate !== bDate) return bDate.localeCompare(aDate);
      return Number(b.proposal_id || 0) - Number(a.proposal_id || 0);
    });

  approvedProposals.forEach((proposal) => {
    const dangerCauseId = Number(proposal.approved_danger_cause_id);
    const current = map.get(dangerCauseId);
    if (!current) {
      map.set(dangerCauseId, { count: 1, latest: proposal });
      return;
    }
    current.count += 1;
  });

  return map;
};

function SourceChip({ row }) {
  const isWorkerProposal = Number(row.worker_proposal_source) === 1;

  return (
    <Chip
      size="small"
      color={isWorkerProposal ? "warning" : "default"}
      variant={isWorkerProposal ? "filled" : "outlined"}
      icon={isWorkerProposal ? <PersonSearchIcon /> : undefined}
      label={isWorkerProposal ? "근로자 제안" : "일반 등록"}
      sx={{
        fontWeight: FONT_PROPS.SEMIBOLD,
        "& .MuiChip-icon": { fontSize: 16 },
      }}
    />
  );
}

//////////////////////////////////
// 기계/기구 유해·위험요인 등록 컴포넌트
//////////////////////////////////
const PARENT_TITLE = "등록관리";
const CHILD_TITLE = "기계/기구 유해·위험요인 등록";
const REGISTER_CAUSE_AUTOPILOT_STEPS = [
  {
    target: '[data-autopilot="register-cause-actions"]',
    title: "기계/기구별 유해·위험요인 연결 시작",
    description:
      "이 화면은 설비·공정 연결 항목을 먼저 고르고, 오른쪽에서 유해·위험요인을 선택해 평가 대상으로 연결하는 흐름입니다. 안내 중에는 저장하지 않습니다.",
  },
  {
    target: '[data-autopilot="register-cause-asset-list"]',
    title: "설비·공정 선택",
    description:
      "왼쪽 목록에서 유해·위험요인을 붙일 설비·공정 조합을 선택합니다. 같은 유해·위험요인을 중복 연결하지 않도록 FE와 BE에서 확인합니다.",
  },
  {
    target: '[data-autopilot="register-cause-situation-list"]',
    title: "유해·위험요인 선택",
    description:
      "오른쪽 위험발생상황 및 결과 목록에서 연결할 유해·위험요인을 고릅니다. 근로자 제안에서 승인된 마스터 항목도 이 목록에 함께 표시됩니다.",
  },
  {
    target: '[data-autopilot="register-cause-actions"]',
    title: "평가 대상으로 연결",
    description:
      "선택한 설비·공정과 유해·위험요인을 연결하면 현재 평가 주기의 미평가 항목으로 이어집니다. 시연에서는 위치와 흐름만 확인하면 됩니다.",
  },
];
export function RegisterCause({ embedded = false }) {
  const { group_code, project_code } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isReadOnly = false;

  const UPDATE_STATUS = Object.freeze({
    // 수정 상태
    EDIT: "edit",
    DELETE: "delete",
  });

  const FILTER_STATUS = Object.freeze({
    // 필터 상태
    ALL: "all",
    REGISTERED: "registered",
  });

  const requestedSituation = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const selectedSituation = location.state?.selectedSituation || {};
    return {
      danger_cause_id:
        params.get("danger_cause_id") || selectedSituation.danger_cause_id || "",
      danger_category:
        params.get("danger_category") || selectedSituation.danger_category || "",
      danger_cause: params.get("danger_cause") || selectedSituation.danger_cause || "",
      keyword:
        params.get("keyword") || selectedSituation.danger_description || "",
      danger_description:
        selectedSituation.danger_description || params.get("keyword") || "",
      worker_proposal_source: selectedSituation.worker_proposal_source || "",
      latest_worker_proposal_id:
        params.get("proposal_id") ||
        selectedSituation.latest_worker_proposal_id ||
        "",
      location_id: params.get("location_id") || selectedSituation.location_id || "",
      machine_id: params.get("machine_id") || selectedSituation.machine_id || "",
      location_name:
        selectedSituation.location_name || params.get("location_name") || "",
      machine_name:
        selectedSituation.machine_name || params.get("machine_name") || "",
    };
  }, [location.search, location.state]);

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 데이터 조회에 필요한 조건을 상태로 관리합니다.
  const [searchStatus, setSearchStatus] = useState({
    filter: FILTER_STATUS.REGISTERED,
  });
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [autopilotStepIndex, setAutopilotStepIndex] = useState(0);
  // 데이터 조회에 필요한 쿼리스트링을 만듭니다.
  const qeuryString = {
    params: {
      // 쿼리스트링 만들때 params 이름을 사용하는게 규칙입니다.
      danger_category: searchStatus.danger_category,
      danger_cause: searchStatus.danger_cause,
      globalDBFilter: searchStatus.globalDBFilter,
      filter: searchStatus.filter,
    },
  };

  // 데이터 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getRegisterCause",
    group_code,
    project_code,
    qeuryString
  );
  // 데이터 생성 요청
  const postData = useCreateTableData(
    "request",
    "/postRegisterCause",
    group_code,
    project_code
  );
  // 데이터 수정 요청
  const putData = usePutTableData(
    "request",
    "/putRegisterCause",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableData(
    "request",
    "/deleteRegisterCause",
    group_code,
    project_code
  );

  // 위험발생상황 및 결과 데이터 조회 요청
  const getSituationData = useGetTableDataInQueryString(
    "request",
    "/getSafeSituation",
    group_code,
    project_code,
    qeuryString
  );
  const getWorkerProposalData = useGetTableData(
    "request",
    "/worker-hazard-proposals",
    group_code,
    project_code
  );
  // 위험성평가 설비 등록 데이터 조회 요청
  const getRegisterAssetData = useGetTableDataInQueryString(
    "request",
    "/getRegisterAsset",
    group_code,
    project_code,
    qeuryString
    //{ params: { filter: "registered" } }, // 유지 항목만 조회
  );
  // 위험발생상황 및 결과 필터링 목록 조회 요청
  const getSituationFilterListData = useGetTableData(
    "request",
    "/getSafeSituationFilterList",
    group_code,
    project_code
  );

  // 위험발생상황 및 결과 데이터 생성 요청
  const postSituationData = useCreateTableData(
    "request",
    "/postSafeSituation",
    group_code,
    project_code
  );
  // 위험발생상황 및 결과 데이터 수정 요청
  const putSituationData = usePutTableData(
    "request",
    "/putSafeSituation",
    group_code,
    project_code
  );

  /**
   * tempData 데이터 구조
   * [
       {
          registerAssetData: { register_asset_id: 1, ... },
          situationData: 
          [
            { danger_cause_id: 1, ... }, 
            { danger_cause_id: 2, ... }, 
             ...
          ] 
        },
        ...
      ]
  */

  // 데이터 가공 (삭제된 항목은 배경색 변경)
  const [tempData, setTempData] = useState([]);
  useEffect(() => {
    if (!getRegisterAssetData.data || !getData.data) return;

    const originalRowData = deepCopy(getData.data);

    // 1. originalData를 register_asset_id 기준으로 그룹핑
    const situationDataMap = originalRowData.reduce((acc, cur) => {
      const {
        danger_category_id,
        danger_category,
        danger_cause_id,
        danger_cause,
        danger_description,
        register_cause_id,
        register_cause_create_date,
        register_cause_delete_date,
        register_cause_status,
        delete_reason,
        register_asset_id,
        register_asset_delete_date,
        register_asset_status,
        process_delete_date,
        process_status,
      } = cur;
      if (!acc[register_asset_id]) acc[register_asset_id] = [];
      acc[register_asset_id].push({
        register_cause_id,
        delete_reason,
        register_cause_create_date,
        register_cause_delete_date,
        register_cause_status,
        register_asset_id,
        register_asset_delete_date,
        register_asset_status,
        process_delete_date,
        process_status,
        danger_category_id,
        danger_category,
        danger_cause_id,
        danger_cause,
        danger_description,
      });

      return acc;
    }, {});

    // 2. getRegisterAssetData.data를 기준으로 전체 situationData 유지
    const grouped = getRegisterAssetData.data.map((asset) => {
      //console.log(asset);
      return {
        id: `register-asset-${asset.register_asset_id}`,
        registerAssetData: {
          register_asset_id: asset.register_asset_id,
          process_id: asset.process_id,
          process_name: asset.process_name,
          process_description: asset.process_description,
          machine_id: asset.machine_id,
          machine_name: asset.machine_name,
          safe_machine_name: asset.safe_machine_name,
        },
        // originalData에 해당 register_asset_id 있으면 situationData 채우기
        situationData: situationDataMap[asset.register_asset_id] || [],
        // 삭제된 항목 배경색 표시
        __bgColor:
          asset.process_status === 1 || asset.register_asset_status === 1
            ? {
                backgroundColor: "#ffebeb",
                backgroundImage:
                  "repeating-linear-gradient(45deg, #ffcccc 0, #ffcccc 2px, transparent 2px, transparent 6px)",
                backgroundSize: "8px 8px",
              }
            : theme.palette.background.paper,
      };
    });

    const machineSort = new Intl.Collator("ko-KR", {
      numeric: true,
      sensitivity: "base",
    });
    const sorted = [...grouped].sort((a, b) => {
      const aMachine =
        a.registerAssetData.safe_machine_name ||
        a.registerAssetData.machine_name ||
        "";
      const bMachine =
        b.registerAssetData.safe_machine_name ||
        b.registerAssetData.machine_name ||
        "";
      const machineCompare = machineSort.compare(aMachine, bMachine);
      if (machineCompare !== 0) return machineCompare;

      return machineSort.compare(
        a.registerAssetData.process_name || "",
        b.registerAssetData.process_name || ""
      );
    });

    const machineSummaryMap = sorted.reduce((acc, item) => {
      const machineId = item.registerAssetData.machine_id || "unknown";
      if (!acc[machineId]) {
        acc[machineId] = {
          rowCount: 0,
          hazardCount: 0,
        };
      }
      acc[machineId].rowCount += 1;
      acc[machineId].hazardCount += item.situationData.length;
      return acc;
    }, {});

    const machineIndexMap = {};
    const machineFirstMap = {};
    const groupedByMachine = sorted.map((item) => {
      const machineId = item.registerAssetData.machine_id || "unknown";
      machineIndexMap[machineId] = (machineIndexMap[machineId] || 0) + 1;
      const isFirst = !machineFirstMap[machineId];
      machineFirstMap[machineId] = true;

      return {
        ...item,
        machineGroup: {
          isFirst,
          index: machineIndexMap[machineId],
          rowCount: machineSummaryMap[machineId]?.rowCount || 1,
          hazardCount: machineSummaryMap[machineId]?.hazardCount || 0,
        },
      };
    });

    // 원본 / 편집 데이터 보관
    //setBackupData(deepCopy(grouped));
    setTempData(deepCopy(groupedByMachine));
  }, [getData.data, getRegisterAssetData.data, theme.palette.background.paper]);

  const registerAssetSummary = useMemo(() => {
    const machineIds = new Set();
    const processIds = new Set();
    let hazardCount = 0;

    tempData.forEach((item) => {
      const asset = item.registerAssetData || {};
      if (asset.machine_id != null) machineIds.add(asset.machine_id);
      if (asset.process_id != null) processIds.add(asset.process_id);
      hazardCount += item.situationData?.length || 0;
    });

    return {
      machineCount: machineIds.size,
      assetCount: tempData.length,
      processCount: processIds.size,
      hazardCount,
    };
  }, [tempData]);

  // 필터 초기값 설정
  useEffect(() => {
    // 데이터가 로드되지 않았거나 비어 있는 경우 처리 중단
    if (
      !getSituationFilterListData.data ||
      getSituationFilterListData.data.length === 0
    ) {
      return;
    }
    //console.log(getSituationFilterListData.data);

    const firstCategory = getSituationFilterListData.data[0];
    const firstCause = firstCategory.danger_cause_filters[0];
    const requestedCategoryExists = getSituationFilterListData.data.some(
      (item) => item.danger_category_filter === requestedSituation.danger_category
    );
    const requestedCauseExists =
      requestedCategoryExists &&
      getSituationFilterListData.data
        .find((item) => item.danger_category_filter === requestedSituation.danger_category)
        ?.danger_cause_filters.some(
          (filter) => filter.danger_cause === requestedSituation.danger_cause
        );

    const data = {
      globalDBFilter: "", // 전체 검색 텍스트 초기값 비워두기
      danger_category: requestedCategoryExists
        ? requestedSituation.danger_category
        : firstCategory.danger_category_filter,
      danger_cause: requestedCauseExists
        ? requestedSituation.danger_cause
        : firstCause.danger_cause,
      filter: "registered",
    };
    if (requestedSituation.keyword) {
      data.globalDBFilter = requestedSituation.keyword;
    }

    setSearchStatus(data);
    if (requestedSituation.keyword) {
      setSituationGlobalDBFilter(requestedSituation.keyword);
    }
  }, [getSituationFilterListData.data, requestedSituation]);

  // 데이터 업데이트 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: "",
    severity: "", // 'success' | 'error' | 'warning' | 'info'
  });
  const openValidationAlert = (message, severity = "success") => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = () => {
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: "",
      severity: "",
    });
  };

  // 메시지 박스의 알람 메시지
  const [messageAlarm, setMessageAlarm] = useState({
    isOpen: false,
    title: "",
    message: "",
  });
  const openMessageAlarm = (title = "", message = "") => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: true,
      title: title,
      message: message,
    });
  };
  const closeMessageAlarm = () => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: false,
      title: "",
      message: "",
    });
  };

  // 위험분류 목록
  const dangerCategoryList = Array.from(
    new Set(
      getSituationFilterListData.data.map((item) => item.danger_category_filter)
    )
  );

  // 위험원인 목록
  const dangerCauseList = getSituationFilterListData.data
    .filter((item) => {
      return item.danger_category_filter === searchStatus.danger_category;
    })
    .flatMap((item) => {
      return item.danger_cause_filters.map((filter) => filter.danger_cause);
    });
  // 위험원인 목록을 위험분류에 따라 동적으로 가져오는 함수
  const getDangerCause = (dangerCategory) => {
    const index = getSituationFilterListData.data.findIndex(
      (item) => item.danger_category_filter === dangerCategory
    );
    if (index !== -1) {
      return getSituationFilterListData.data[index].danger_cause_filters;
    } else return null;
  };

  // 렌더링 상태 관리
  const RenderState = Object.freeze({
    VIEW: "viewMode", // 조회 모드
    EDIT: "editMode", // 편집 모드
    // ADMIN: "adminMode", // 관리자 모드
  });
  const [mode, setMode] = useState(RenderState.VIEW);
  const [deleteMode, setDeleteMode] = useState(false);
  const changeModeState = (newState) => {
    setMode(newState);
    if (newState !== RenderState.EDIT) setDeleteMode(false);
  };

  const enterAddMode = () => {
    if (isReadOnly) return;
    setDeleteMode(false);
    changeModeState(RenderState.EDIT);
  };

  const toggleDeleteMode = () => {
    if (isReadOnly) return;
    setDeleteMode((prev) => {
      const next = !prev;
      if (next) {
        setMode(RenderState.VIEW);
        clearEditItemSelect();
        clearRegisterCauseSelection();
        clearSituationSelection();
      }
      return next;
    });
  };

  useEffect(() => {
    if (isReadOnly) return;
    if (requestedSituation.danger_cause_id || requestedSituation.keyword) {
      setMode(RenderState.EDIT);
      setSituationSourceFilter(requestedSituation.danger_cause_id ? "worker" : "all");
    }
  }, [RenderState.EDIT, isReadOnly, requestedSituation.danger_cause_id, requestedSituation.keyword]);

  // 선택한 편집 항목 관리
  const [selectedEditItems, setSelectedEditItems] = useState({
    registerAssetIds: [],
  });
  const [recentRegisterCause, setRecentRegisterCause] = useState({
    assetIds: [],
    dangerCauseIds: [],
  });
  const [connectionFeedback, setConnectionFeedback] = useState(null);
  const setEditItemSelect = ({ registerAssetIds }) =>
    setSelectedEditItems({ registerAssetIds });
  const clearEditItemSelect = () =>
    setSelectedEditItems({ registerAssetIds: [] });

  // 공정과 기계/기구 매칭 등록 테이블 참조
  const registerCauseTableRef = useRef();
  const clearRegisterCauseSelection = () => {
    registerCauseTableRef.current?.clearSelection();
  };
  const getRegisterCauseSelectedRows = () => {
    return registerCauseTableRef.current?.getSelectedRows();
  };

  // 공정과 기계/기구 매칭 등록 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(""); // 검색 텍스트 상태
  const [registerAssetFilter, setRegisterAssetFilter] = useState("all");
  const filteredRegisterCauseData = useMemo(() => {
    if (registerAssetFilter === "connected") {
      return tempData.filter((item) => (item.situationData || []).length > 0);
    }
    if (registerAssetFilter === "empty") {
      return tempData.filter((item) => (item.situationData || []).length === 0);
    }
    return tempData;
  }, [registerAssetFilter, tempData]);

  const customRegisterCauseTableTopToolbar = (table) => {
    const data = table.getSelectedRowModel().rows.map((row) => row.original);
    const hasLinkedData = table
      .getRowModel()
      .rows.some((row) => Array.isArray(row.original?.situationData) && row.original.situationData.length > 0);
    const isDeleteDisabled = !hasLinkedData;

    return (
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        sx={{
          gap: 1.5,
          flexWrap: { xs: "wrap", xl: "nowrap" },
        }}
      >
        {/* 왼쪽 영역 */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{ minWidth: 0, flex: "1 1 auto" }}
        >
          <Typography
            sx={{
              fontWeight: FONT_PROPS.SEMIBOLD,
              fontSize: 14,
              color: theme.palette.text.primary,
            }}
          >
            설비 기준 연결
          </Typography>
          <Chip size="small" variant="outlined" label={`설비 ${registerAssetSummary.machineCount}대`} />
          <Chip size="small" variant="outlined" label={`연결 ${registerAssetSummary.assetCount}건`} />
          <Chip size="small" variant="outlined" label={`유해·위험요인 ${registerAssetSummary.hazardCount}건`} />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {REGISTER_ASSET_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              size="small"
              variant={registerAssetFilter === filter.value ? "contained" : "outlined"}
              color="primary"
              onClick={() => setRegisterAssetFilter(filter.value)}
              sx={{
                minWidth: "max-content",
                height: 28,
                px: 1.2,
                borderRadius: "7px",
                fontWeight: FONT_PROPS.SEMIBOLD,
                boxShadow: "none",
                whiteSpace: "nowrap",
              }}
            >
              {filter.label}
            </Button>
          ))}
          {data.length > 0 ? (
            <Chip size="small" color="primary" label={`선택 ${data.length}건`} />
          ) : null}
        </Box>

        {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
        <Box
          data-autopilot="register-cause-actions"
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            flex: "0 0 auto",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            "& .MuiButton-root": {
              whiteSpace: "nowrap",
              minWidth: "max-content",
              writingMode: "horizontal-tb",
            },
            "& .MuiButton-startIcon": {
              mr: 0.75,
            },
          }}
        >
          {/* 검색창 */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={() => setShowSearch((prev) => !prev)}>
              <SearchIcon />
            </IconButton>

            <Collapse in={showSearch} orientation="horizontal">
              <TextField
                size="small"
                placeholder="검색"
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  table.setGlobalFilter(e.target.value);
                }}
                sx={{ minWidth: 200 }}
                variant="outlined"
                slots={{
                  input: OutlinedInput,
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setGlobalFilter("");
                            table.setGlobalFilter("");
                            setShowSearch(false); // 닫기
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Collapse>
          </Box>

          {mode !== RenderState.EDIT ? (
            <Tooltip title="오토파일럿">
              <IconButton
                color="primary"
                onClick={() => {
                  setAutopilotStepIndex(0);
                  setAutopilotOpen(true);
                }}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                <PlayCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          ) : null}

          {/* 추가 */}
          {!isReadOnly && mode === RenderState.VIEW && (
            <Buttons.Custom
              onClick={() => {
                enterAddMode();
              }}
              startIcon={<AddIcon />}
              sx={{ padding: "10px 14px" }}
            >
              추가
            </Buttons.Custom>
          )}

          {/*
            관리자 모드 토글은 현재 화면에서 사용하지 않도록 주석처리합니다.
            필요 시 아래 블록과 admin 컬럼/편집 옵션을 함께 복구하면 됩니다.

            {mode === RenderState.ADMIN && (
              <Buttons.Delete
                onClick={() => {
                  openDeleteRecordModal(
                    table.getSelectedRowModel().rows.map((row) => row.original)
                  );
                }}
                disabled={isDeleteDisabled}
                sx={{ padding: "12px 16px" }}
              >
                데이터 삭제
              </Buttons.Delete>
            )}
            <FormControl component="fieldset">
              <FormControlLabel
                value="top"
                control={
                  <Switch
                    color="primary"
                    checked={mode === RenderState.ADMIN}
                    onChange={(event) => {
                      const state = event.target.checked
                        ? RenderState.ADMIN
                        : RenderState.VIEW;
                      changeModeState(state);

                      setSearchStatus({
                        ...searchStatus,
                        filter: event.target.checked
                          ? FILTER_STATUS.ALL
                          : FILTER_STATUS.REGISTERED,
                      });
                      clearEditItemSelect();
                      clearRegisterCauseSelection();
                    }}
                  />
                }
                label="관리자모드"
                labelPlacement="end"
                sx={{ marginLeft: "4px" }}
              />
            </FormControl>
          */}

          {/* 삭제 모드 */}
          {!isReadOnly && mode !== RenderState.EDIT ? (
            <Buttons.Delete
              onClick={toggleDeleteMode}
              disabled={isDeleteDisabled}
              sx={{
                padding: "10px 14px",
                bgcolor: deleteMode ? "#fee2e2 !important" : "#fff5f5 !important",
                color: "#b91c1c !important",
                border: "1px solid #fecaca",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#fee2e2 !important",
                  boxShadow: "none",
                },
              }}
            >
              {deleteMode ? "삭제 모드 종료" : "삭제 모드"}
            </Buttons.Delete>
          ) : null}
        </Box>
      </Box>
    );
  };

  // 위험발생상황 및 결과 테이블 참조
  const situationTableRef = useRef();
  const clearSituationSelection = () => {
    situationTableRef.current?.clearSelection();
  };
  const getSituationSelectedRows = () => {
    return situationTableRef.current?.getSelectedRows();
  };

  // 위험발생상황 및 결과 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSituationSearch, setShowSituationSearch] = useState(false); // 검색창 열림 여부
  const [situationGlobalFilter, setSituationGlobalFilter] = useState(""); // 검색 텍스트 상태
  const [situationGlobalDBFilter, setSituationGlobalDBFilter] = useState(""); // 데이터베이스 검색 텍스트 상태
  const [situationSourceFilter, setSituationSourceFilter] = useState(
    requestedSituation.danger_cause_id ? "worker" : "all"
  );

  const workerProposalSourceMap = useMemo(
    () => buildWorkerProposalSourceMap(getWorkerProposalData.data),
    [getWorkerProposalData.data]
  );

  const situationDataWithSource = useMemo(() => {
    return (getSituationData.data || []).map((row) => {
      const proposalSource = workerProposalSourceMap.get(Number(row.danger_cause_id));
      const latest = proposalSource?.latest;
      const isWorkerSource =
        Number(row.worker_proposal_source) === 1 || Boolean(proposalSource);

      return {
        ...row,
        worker_proposal_source: isWorkerSource ? 1 : 0,
        worker_proposal_count:
          Number(row.worker_proposal_count || 0) || proposalSource?.count || 0,
        latest_worker_proposal_id:
          row.latest_worker_proposal_id || latest?.proposal_id || null,
        latest_worker_proposer_name:
          row.latest_worker_proposer_name || latest?.proposer_name || null,
        latest_worker_proposal_text:
          row.latest_worker_proposal_text || latest?.proposal_text || null,
        latest_worker_reviewed_at:
          row.latest_worker_reviewed_at || latest?.reviewed_at || null,
        __bgColor: isWorkerSource ? "#fffaf0" : row.__bgColor,
      };
    });
  }, [getSituationData.data, workerProposalSourceMap]);

  const filteredSituationData = useMemo(() => {
    if (situationSourceFilter === "worker") {
      return situationDataWithSource.filter((row) => Number(row.worker_proposal_source) === 1);
    }
    if (situationSourceFilter === "manual") {
      return situationDataWithSource.filter((row) => Number(row.worker_proposal_source) !== 1);
    }
    return situationDataWithSource;
  }, [situationDataWithSource, situationSourceFilter]);

  const requestedSituationRow = useMemo(() => {
    if (!requestedSituation.danger_cause_id && !requestedSituation.keyword) return null;
    return situationDataWithSource.find((row) => {
      if (requestedSituation.danger_cause_id) {
        return Number(row.danger_cause_id) === Number(requestedSituation.danger_cause_id);
      }
      return row.danger_description === requestedSituation.keyword;
    });
  }, [requestedSituation, situationDataWithSource]);

  const requestedConnectionSummary = useMemo(() => {
    if (!requestedSituationRow) {
      return { selected: 0, connected: 0, connectable: 0, connectedAssets: [] };
    }

    const selectedAssetIds = new Set(selectedEditItems.registerAssetIds);
    const selectedAssets = tempData.filter((item) =>
      selectedAssetIds.has(item.registerAssetData.register_asset_id)
    );
    const connectedAssets = selectedAssets.filter((item) =>
      item.situationData.some(
        (situation) =>
          Number(situation.danger_cause_id) === Number(requestedSituationRow.danger_cause_id)
      )
    );

    return {
      selected: selectedAssets.length,
      connected: connectedAssets.length,
      connectable: selectedAssets.length - connectedAssets.length,
      connectedAssets,
    };
  }, [requestedSituationRow, selectedEditItems.registerAssetIds, tempData]);

  const requestedMachineConnectionSummary = useMemo(() => {
    if (!requestedSituationRow || !requestedSituation.machine_id) {
      return {
        matched: 0,
        connected: 0,
        connectable: 0,
        connectableAssetIds: [],
      };
    }

    const matchedAssets = tempData.filter(
      (item) =>
        Number(item.registerAssetData?.machine_id) ===
        Number(requestedSituation.machine_id)
    );
    const connectedAssets = matchedAssets.filter((item) =>
      item.situationData.some(
        (situation) =>
          Number(situation.danger_cause_id) ===
          Number(requestedSituationRow.danger_cause_id)
      )
    );
    const connectableAssets = matchedAssets.filter(
      (item) =>
        !item.situationData.some(
          (situation) =>
            Number(situation.danger_cause_id) ===
            Number(requestedSituationRow.danger_cause_id)
        )
    );

    return {
      matched: matchedAssets.length,
      connected: connectedAssets.length,
      connectable: connectableAssets.length,
      connectableAssetIds: connectableAssets.map(
        (item) => item.registerAssetData.register_asset_id
      ),
    };
  }, [requestedSituation.machine_id, requestedSituationRow, tempData]);

  const situationColumns = useMemo(() => {
    const descriptionColumn = standardSituationColumns.find(
      (column) => column.accessorKey === "danger_description"
    );
    const otherColumns = standardSituationColumns.filter(
      (column) => column.accessorKey !== "danger_description"
    );

    return [
      {
        accessorKey: "worker_proposal_source",
        header: "출처",
        size: 140,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => (
          <Stack alignItems="center" spacing={0.5}>
            <SourceChip row={row.original} />
            {Number(row.original.worker_proposal_count) > 1 ? (
              <Typography variant="caption" color="text.secondary">
                {row.original.worker_proposal_count}건
              </Typography>
            ) : null}
          </Stack>
        ),
      },
      ...otherColumns,
      {
        ...descriptionColumn,
        Cell: ({ row }) => {
          const original = row.original;
          const isRequested =
            requestedSituation.danger_cause_id &&
            Number(original.danger_cause_id) === Number(requestedSituation.danger_cause_id);
          const isWorkerProposal = Number(original.worker_proposal_source) === 1;
          return (
            <Box sx={{ py: 0.75, pr: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                {isRequested ? <Chip size="small" color="primary" label="전달됨" /> : null}
                {isWorkerProposal ? <Chip size="small" color="warning" variant="outlined" label="근로자 제안" /> : null}
              </Stack>
              <Typography
                variant="body2"
                sx={{ fontWeight: FONT_PROPS.MEDIUM, lineHeight: 1.55, wordBreak: "keep-all" }}
              >
                {original.danger_description}
              </Typography>
              {isWorkerProposal ? (
                <Tooltip title={original.latest_worker_proposal_text || ""} placement="top-start">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    최근 제안 #{original.latest_worker_proposal_id || "-"} ·{" "}
                    {original.latest_worker_proposer_name || "제안자 미확인"} ·{" "}
                    {normalizeDateText(original.latest_worker_reviewed_at) || "검토일 미확인"}
                  </Typography>
                </Tooltip>
              ) : null}
            </Box>
          );
        },
      },
    ];
  }, [requestedSituation.danger_cause_id]);

  const customSituationTableTopToolbar = (table) => (
    <Box
      width="100%"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      rowGap={2}
      p={2}
    >
      {/* 왼쪽 영역 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          maxWidth: "100%",
          overflowX: "auto",
          whiteSpace: "nowrap",
        }}
      >
        {/* 위험분류 카테고리 */}
        <FormControl size="small">
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                fontWeight: FONT_PROPS.SEMIBOLD,
                fontSize: 14,
                color: theme.text,
              }}
            >
              위험분류
            </Typography>
            <Select
              value={searchStatus.danger_category || ""}
              displayEmpty
              onChange={(e) => {
                setSearchStatus({
                  ...searchStatus,
                  danger_category: e.target.value,
                  danger_cause:
                    getDangerCause(e.target.value)?.[0].danger_cause || "", // 위험원인 초기화
                  globalDBFilter: "", // 전체 검색 텍스트 초기화
                });
              }}
              sx={{
                width: "130px",
                fontWeight: FONT_PROPS.SEMIBOLD,
                color: theme.text,
                fontSize: 14,
              }}
            >
              {/* 필터 목록 */}
              {dangerCategoryList.map((category, index) => (
                <MenuItem
                  key={index}
                  value={category}
                  sx={{
                    fontWeight: FONT_PROPS.SEMIBOLD,
                    color: theme.text,
                    fontSize: 14,
                  }}
                >
                  {category}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 위험원인 카테고리 */}
        <FormControl size="small">
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                fontWeight: FONT_PROPS.SEMIBOLD,
                fontSize: 14,
                color: theme.text,
              }}
            >
              위험원인
            </Typography>
            <Select
              value={searchStatus.danger_cause || ""}
              displayEmpty
              onChange={(e) => {
                setSearchStatus({
                  ...searchStatus,
                  danger_cause: e.target.value,
                  globalDBFilter: "", // 전체 검색 텍스트 초기화
                });
              }}
              sx={{
                width: "180px",
                fontWeight: FONT_PROPS.SEMIBOLD,
                color: theme.text,
                fontSize: 14,
              }}
            >
              {/* 필터 목록 */}
              {dangerCauseList.map((cause, index) => (
                <MenuItem
                  key={index}
                  value={cause}
                  sx={{
                    fontWeight: FONT_PROPS.SEMIBOLD,
                    color: theme.text,
                    fontSize: 14,
                  }}
                >
                  {cause}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 구분선 */}
        <Divider
          orientation="vertical"
          sx={{
            height: 40, // 원하는 높이
            //borderRightWidth: 2,
            borderColor: theme.palette.divider,
            //marginX: 0.5, // 좌우 간격
          }}
        />
        {/* 전체 검색 필드 */}
        <FormControl size="small">
          <TextField
            size="small"
            placeholder="위험발생상황 및 결과 전체 검색"
            variant="outlined"
            value={situationGlobalDBFilter}
            onChange={(e) => {
              setSituationGlobalDBFilter(e.target.value);
            }}
          />
        </FormControl>
        {/* 전체 검색 버튼 */}
        <Buttons.Custom
          startIcon={<SearchIcon />}
          onClick={() => {
            if (situationGlobalDBFilter === "") {
              openMessageAlarm("안내", "전체 검색 전, 검색어를 입력하세요.");
              return;
            }
            setSearchStatus({
              ...searchStatus,
              globalDBFilter: situationGlobalDBFilter,
            });
            //console.log("전체 검색 클릭됨");
          }}
          sx={{ padding: "12px 16px", fontSize: 14 }}
        >
          전체 검색
        </Buttons.Custom>
      </Box>

      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {SOURCE_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              size="small"
              variant={situationSourceFilter === filter.value ? "contained" : "outlined"}
              color={filter.value === "worker" ? "warning" : "primary"}
              onClick={() => setSituationSourceFilter(filter.value)}
              sx={{ borderRadius: 99, fontWeight: FONT_PROPS.SEMIBOLD }}
            >
              {filter.label}
            </Button>
          ))}
        </Stack>
        {/* 검색창 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => setShowSituationSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>

          <Collapse in={showSituationSearch} orientation="horizontal">
            <TextField
              size="small"
              placeholder="검색"
              value={situationGlobalFilter}
              onChange={(e) => {
                setSituationGlobalFilter(e.target.value);
                table.setGlobalFilter(e.target.value);
              }}
              sx={{ minWidth: 200 }}
              variant="outlined"
              slots={{
                input: OutlinedInput,
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setSituationGlobalFilter("");
                          table.setGlobalFilter("");
                          setShowSituationSearch(false); // 닫기
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Collapse>
        </Box>
        {/* 버튼 */}
        <Buttons.Add
          onClick={() => openAddSituationModal()}
          sx={{ padding: "12px 16px" }}
        />
      </Box>
    </Box>
  );
  // 위험발생상황 및 결과 테이블의 하단 툴바 커스터마이징 관련 내용
  const customSituationTableBottomToolbar = (table) => {
    const allRows = table.getRowModel().rows.length;
    const selectedRows = table.getSelectedRowModel().rows.length;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        {/** 왼쪽 영역 */}
        <Box sx={{ display: "flex", flex: 1, alignItems: "center" }}>
          {/** 선택 초기화 */}
          {selectedRows > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                px: 6,
                py: 1,
              }}
            >
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: FONT_PROPS.REGULAR,
                  fontSize: 14,
                  color: theme.palette.text,
                }}
              >
                {allRows} 행 중 {selectedRows} 행 선택됨
              </Typography>
              <Typography
                onClick={() => clearSituationSelection()}
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: FONT_PROPS.MEDIUM,
                  fontSize: 13,
                  cursor: "pointer",
                  "&:hover": {
                    color: theme.palette.primary.dark,
                    backgroundColor: theme.palette.primary.lighterOpacity,
                  },
                }}
              >
                선택 초기화
              </Typography>
            </Box>
          )}
        </Box>

        {/** 오른쪽 영역 */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {requestedSituationRow ? (
            <Buttons.Custom
              onClick={() => {
                addSituation({
                  registerAssetIds: selectedEditItems.registerAssetIds,
                  forcedSituations: [requestedSituationRow],
                });
              }}
              disabled={
                selectedEditItems.registerAssetIds.length === 0 ||
                requestedConnectionSummary.connectable === 0
              }
              startIcon={<AssignmentTurnedInIcon />}
              sx={{ padding: "12px 16px" }}
            >
              {requestedConnectionSummary.connectable === 0 &&
              requestedConnectionSummary.selected > 0
                ? "이미 연결됨"
                : "전달 항목 바로 추가"}
            </Buttons.Custom>
          ) : null}
          {/** 추가 버튼 */}
          <Buttons.Custom
            onClick={() => {
              addSituation({
                registerAssetIds: selectedEditItems.registerAssetIds,
              });
            }}
            disabled={selectedEditItems.registerAssetIds.length === 0}
            startIcon={<KeyboardDoubleArrowLeftIcon />}
            sx={{ padding: "12px 16px" }}
          >
            추가
          </Buttons.Custom>

          {/** 닫기 버튼 */}
          <Buttons.Custom
            onClick={() => {
              changeModeState(RenderState.VIEW);
              clearEditItemSelect();
              clearRegisterCauseSelection();
              clearSituationSelection();
            }}
            startIcon={<CloseIcon />}
            sx={{ padding: "12px 16px" }}
          >
            닫기
          </Buttons.Custom>
        </Box>
      </Box>
    );
  };

  // 위험발생상황 및 결과 추가
  const addSituation = ({ registerAssetIds, forcedSituations = null }) => {
    // 선택한 항목
    const selectedSituations =
      forcedSituations ||
      getSituationSelectedRows().map(
        (row) => row.original
      );
    const isRequestedConnection = Boolean(forcedSituations?.length);

    // 선택 체크
    if (selectedSituations.length <= 0) {
      openValidationAlert(message.retrySelectedSituationList, "error");
      return;
    }

    const updateData = tempData.reduce((acc, item) => {
      if (!registerAssetIds.includes(item.registerAssetData.register_asset_id))
        return acc;

      // 새로 추가된 situation 추출
      const newSituations = selectedSituations.filter(
        (s) =>
          !item.situationData.some(
            (os) => os.danger_cause_id === s.danger_cause_id
          )
      );

      if (newSituations.length > 0) {
        acc.push({
          registerAssetData: item.registerAssetData,
          situationData: newSituations,
        });
      }
      return acc;
    }, []);

    // 추가된 항목이 있다면 업데이트
    if (updateData.length > 0) {
      addRegisterCauseData(updateData, {
        isRequestedConnection,
        situation: selectedSituations[0],
      });
    } else {
      openValidationAlert("선택한 설비·공정에 이미 연결된 유해·위험요인입니다.", "info");
      if (isRequestedConnection) {
        setConnectionFeedback({
          type: "duplicate",
          situation: selectedSituations[0],
          count: registerAssetIds.length,
        });
      }
    }

    // ✅ 중복이 있든 없든 항상 선택 해제
    clearSituationSelection();
  };

  // DB 업데이트 (추가 데이터)
  const addRegisterCauseData = async (updateData, context = {}) => {
    try {
      //console.log("편집 데이터: ", updateData);

      // 데이터베이스 업데이트
      const result = await postData.mutateAsync(updateData);
      if (result.status === 200) {
        setRecentRegisterCause({
          assetIds: updateData
            .map((item) => Number(item.registerAssetData?.register_asset_id))
            .filter(Number.isFinite),
          dangerCauseIds: Array.from(
            new Set(
              updateData
                .flatMap((item) => item.situationData || [])
                .map((item) => Number(item.danger_cause_id))
                .filter(Number.isFinite)
            )
          ),
        });
        openValidationAlert(
          context.isRequestedConnection
            ? "선택한 설비·공정에 유해·위험요인을 연결했습니다."
            : message.addSuccess
        );
        if (context.isRequestedConnection) {
          setConnectionFeedback({
            type: "success",
            situation: context.situation,
            count: updateData.length,
          });
        }
      }

      // 생성 모달 초기화
      //closeEditModal();
    } catch (error) {
      console.error("데이터 추가 중 오류 발생:", error);
    }
  };

  // 기계/기구 위험요인 등록 수정 모달 상태 관리
  const [editModal, setEditModal] = useState({ isOpen: false, rowData: [] });
  // 행 수정 기능은 현재 화면에서 사용하지 않도록 주석처리합니다.
  // const openEditModal = (rowData) =>
  //   setEditModal({ isOpen: true, rowData: rowData });
  const closeEditModal = () => setEditModal({ isOpen: false, rowData: [] });
  // 기계/기구 유해위험요인 등록 테이블의 행 수정 액션
  // const customRenderRowActionContent = (rowData) => {
  //   return (
  //     <Box sx={{ width: 80 }}>
  //       <Buttons.Edit
  //         onClick={(event) => {
  //           event.stopPropagation(); // 이벤트 전파 중단
  //           openEditModal(rowData);
  //         }}
  //         sx={{ padding: "8px 12px" }}
  //       />
  //     </Box>
  //   );
  // };
  // DB 업데이트 (수정 데이터)
  const updateEditData = async (updateData) => {
    //console.log(updateData);
    try {
      const data = {
        updateData: updateData,
        updateStatus: UPDATE_STATUS.EDIT,
      };

      // 데이터베이스 업데이트
      const result = await putData.mutateAsync(data);
      if (result.status === 200) {
        openValidationAlert(message.updateEditSuccess);
      }

      // 수정 모달 초기화
      closeEditModal();
    } catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  };

  // 기계/기구 위험요인 등록 삭제 모달 상태 관리
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    deleteItem: null,
    deleteReason: "",
  });
  const openDeleteModal = (item) =>
    setDeleteModal({ isOpen: true, deleteItem: item, deleteReason: "" });
  const closeDeleteModal = () =>
    setDeleteModal({ isOpen: false, deleteItem: null, deleteReason: "" });

  // DB 업데이트 (논리 삭제 데이터)
  const softDeleteRegisterCauseData = async () => {
    closeDeleteModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {
      const data = {
        updateData: {
          data: deleteModal.deleteItem,
          deleteReason: deleteModal.deleteReason,
        },
        updateStatus: UPDATE_STATUS.DELETE,
      };

      // 데이터베이스 업데이트
      const result = await putData.mutateAsync(data);
      if (result.status === 200) {
        openValidationAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearRegisterCauseSelection();
    } catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

  // 물리 삭제 모달 상태 관리
  const [deleteRecordModal, setDeleteRecordModal] = useState({
    isOpen: false,
    deleteItem: null,
  });
  // 관리자 모드 물리 삭제 진입점은 현재 화면에서 사용하지 않도록 주석처리합니다.
  // const openDeleteRecordModal = (item) =>
  //   setDeleteRecordModal({ isOpen: true, deleteItem: item });
  const closeDeleteRecordModal = () =>
    setDeleteRecordModal({ isOpen: false, deleteItem: null });

  // DB 업데이트 (물리 삭제 데이터)
  const deleteRegisterCauseData = async () => {
    closeDeleteRecordModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {
      const data = deleteRecordModal.deleteItem.flatMap((item) =>
        item.situationData.map((s) => ({
          register_cause_id: s.register_cause_id,
        }))
      );

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        deleteData: data,
      });

      if (result.status === 200) {
        openValidationAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearRegisterCauseSelection();
    } catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

  ///////////////////////////////
  // 위험발생상황 및 결과 추가, 수정
  ///////////////////////////////

  // 위험발생상황 및 결과 추가 모달 상태 관리
  const [showAddSituationModal, setShowAddSituationModal] = useState({
    isOpen: false,
  });
  const openAddSituationModal = () =>
    setShowAddSituationModal({ isOpen: true });
  const closeAddSituationModal = () =>
    setShowAddSituationModal({ isOpen: false });
  // DB 업데이트 (추가 데이터)
  const addSituationData = async (addData) => {
    try {
      //console.log("새로운 데이터: ", addData);

      const data = {
        danger_category: addData.danger_category, // 위험분류
        danger_cause: addData.danger_cause, // 위험원인
        danger_description: addData.danger_description, // 위험요인 발생상황 및 결과
      };

      // 데이터베이스 업데이트
      const result = await postSituationData.mutateAsync(data);
      if (result.status === 200) {
        openValidationAlert(message.addSuccess);
      }

      // 생성 모달 초기화
      closeAddSituationModal();
      // 사본 추가 모달 초기화
      closeSituationCopyModal();
    } catch (error) {
      console.error("데이터 추가 중 오류 발생:", error);
    }
  };

  // 위험발생상황 및 결과 수정 모달 상태 관리
  const [showEditSituationModal, setShowEditSituationModal] = useState({
    isOpen: false,
    rowData: null,
  });
  const openEditSituationModal = (rowData) =>
    setShowEditSituationModal({ isOpen: true, rowData });
  const closeEditSituationModal = () =>
    setShowEditSituationModal({ isOpen: false, rowData: null });

  // 위험발생상황 및 결과 사본 추가 상태 관리
  const [showSituationCopyModal, setShowSituationCopyModal] = useState({
    isOpen: false,
    rowData: null,
  });
  const openSituationCopyModal = (rowData) =>
    setShowSituationCopyModal({ isOpen: true, rowData: rowData });
  const closeSituationCopyModal = () =>
    setShowSituationCopyModal({ isOpen: false, rowData: null });

  // 위험발생상황 및 결과 테이블의 행(row) 수정 액션
  const customEditSituationRowAction = (rowData) => {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
        }}
      >
        <Buttons.Edit
          onClick={(event) => {
            event.stopPropagation(); // 이벤트 전파 중단
            openEditSituationModal(rowData);
          }}
          sx={{ padding: "8px 12px" }}
        />
        <Buttons.Custom
          onClick={(e) => {
            e.stopPropagation(); // 이벤트 전파 중단
            openSituationCopyModal(rowData);
          }}
          startIcon={<ContentCopyIcon />}
          sx={{ padding: "8px 12px" }}
        >
          사본 추가
        </Buttons.Custom>
      </Box>
    );
  };
  // DB 업데이트 (수정 데이터)
  const updateSituationData = async (updateData) => {
    try {
      //console.log(updateData);

      const data = {
        danger_cause_id: updateData.danger_cause_id, // 위험원인 ID
        danger_category: updateData.danger_category, // 위험분류
        danger_cause: updateData.danger_cause, // 위험원인
        danger_description: updateData.danger_description, // 위험요인 발생상황 및 결과
      };

      const result = await putSituationData.mutateAsync(data);
      if (result.status === 200) {
        openValidationAlert(message.updateEditSuccess);
      }

      // 수정 모달 초기화
      closeEditSituationModal();
    } catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  };

  // 렌더링할 컬럼 설정
  const getColums = () => {
    if (deleteMode) return registerCauseColumns_delete;
    if (mode === RenderState.EDIT) return registerCauseColumns_edit;
    // else if (mode === RenderState.ADMIN) return registerCauseColumns_admin;
    return registerCauseColumns;
  };

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    return (
      <TableContainer>
        {requestedSituation.danger_cause_id || requestedSituation.keyword ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "#fff",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip size="small" color="primary" label="마스터에서 전달됨" />
                  {requestedSituation.latest_worker_proposal_id ? (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`근로자 제안 #${requestedSituation.latest_worker_proposal_id}`}
                    />
                  ) : null}
                </Stack>
                <Typography sx={{ fontWeight: 900 }}>
                  {requestedSituation.danger_description || requestedSituation.keyword || "전달된 유해·위험요인"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {requestedSituation.machine_id
                    ? "승인된 제안의 설비를 기준으로 연결 가능한 평가대상을 자동으로 찾았습니다."
                    : "왼쪽에서 연결할 설비·공정을 선택한 뒤 오른쪽 하단의 전달 항목 바로 추가를 누르면 됩니다."}
                </Typography>
                {requestedSituation.machine_id ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    <Chip
                      size="small"
                      color="primary"
                      label={`전달 설비 ${requestedSituation.machine_name || requestedSituation.machine_id}`}
                    />
                    <Chip
                      size="small"
                      color={requestedMachineConnectionSummary.matched ? "default" : "warning"}
                      label={`대상 ${requestedMachineConnectionSummary.matched}건`}
                    />
                    <Chip
                      size="small"
                      color="success"
                      label={`연결 가능 ${requestedMachineConnectionSummary.connectable}건`}
                    />
                    <Chip
                      size="small"
                      color={requestedMachineConnectionSummary.connected > 0 ? "warning" : "default"}
                      label={`이미 연결 ${requestedMachineConnectionSummary.connected}건`}
                    />
                  </Stack>
                ) : requestedConnectionSummary.selected > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    <Chip size="small" color="primary" label={`선택 ${requestedConnectionSummary.selected}개`} />
                    <Chip size="small" color="success" label={`연결 가능 ${requestedConnectionSummary.connectable}개`} />
                    <Chip
                      size="small"
                      color={requestedConnectionSummary.connected > 0 ? "warning" : "default"}
                      label={`이미 연결 ${requestedConnectionSummary.connected}개`}
                    />
                  </Stack>
                ) : null}
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                {requestedSituation.machine_id ? (
                  <Button
                    variant="contained"
                    startIcon={<AssignmentTurnedInIcon />}
                    disabled={
                      !requestedSituationRow ||
                      requestedMachineConnectionSummary.connectable === 0
                    }
                    onClick={() => {
                      addSituation({
                        registerAssetIds:
                          requestedMachineConnectionSummary.connectableAssetIds,
                        forcedSituations: [requestedSituationRow],
                      });
                    }}
                  >
                    전달 설비 바로 연결
                  </Button>
                ) : null}
                <Alert severity="info" sx={{ py: 0.5 }}>
                  현재 화면은 편집 모드로 열렸습니다.
                </Alert>
              </Stack>
            </Stack>
          </Paper>
        ) : null}
        {connectionFeedback ? (
          <Alert
            severity={connectionFeedback.type === "success" ? "success" : "info"}
            sx={{ mb: 1.5, borderRadius: 2 }}
            action={
              <Stack direction="row" spacing={1}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    const params = new URLSearchParams();
                    const dangerCauseId = connectionFeedback.situation?.danger_cause_id;
                    if (dangerCauseId) params.set("danger_cause_id", dangerCauseId);
                    const query = params.toString();
                    navigate(
                      `/safe/${group_code}/${project_code}/assessment/wizard${query ? `?${query}` : ""}`,
                      {
                        state: {
                          selectedSituation: connectionFeedback.situation,
                        },
                      }
                    );
                  }}
                >
                  Wizard에서 평가
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setConnectionFeedback(null);
                    clearRegisterCauseSelection();
                    clearEditItemSelect();
                  }}
                >
                  다른 설비 선택
                </Button>
              </Stack>
            }
          >
            {connectionFeedback.type === "success"
              ? `${connectionFeedback.count}개 설비·공정에 평가대상으로 연결했습니다.`
              : "선택한 설비·공정에는 이미 이 유해·위험요인이 연결되어 있습니다."}
          </Alert>
        ) : null}
        {mode === RenderState.EDIT ? (
          <Box
            sx={{
              mb: 1,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 45fr) minmax(0, 55fr)" },
              gap: 2,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                minHeight: 58,
                px: 1.5,
                py: 1,
                display: "flex",
                alignItems: "center",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", minWidth: 0 }}>
                <Chip size="small" color="primary" label="1st" sx={{ fontWeight: 900 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 900, color: "#0f172a", lineHeight: 1.25 }}>
                    장비/공정 선택
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`선택 ${selectedEditItems.registerAssetIds.length}건`}
                  sx={{ ml: "auto", fontWeight: 900 }}
                />
              </Stack>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                minHeight: 58,
                px: 1.5,
                py: 1,
                display: "flex",
                alignItems: "center",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", minWidth: 0 }}>
                <Chip
                  size="small"
                  label="2nd"
                  sx={{
                    fontWeight: 900,
                    bgcolor: selectedEditItems.registerAssetIds.length > 0 ? "#ecfdf5" : "#f1f5f9",
                    color: selectedEditItems.registerAssetIds.length > 0 ? "#047857" : "#64748b",
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 900, color: "#0f172a", lineHeight: 1.25 }}>
                    유해·위험요인 선택 후 추가
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={() => {
                    changeModeState(RenderState.VIEW);
                    clearEditItemSelect();
                    clearRegisterCauseSelection();
                    clearSituationSelection();
                  }}
                  sx={{
                    flex: "0 0 auto",
                    ml: "auto",
                    borderRadius: "7px",
                    fontWeight: 900,
                    bgcolor: "#fff",
                    borderColor: "#dbe3ef",
                  }}
                >
                  추가 종료
                </Button>
              </Stack>
            </Paper>
          </Box>
        ) : null}
        <Box
          sx={{
            display: mode === RenderState.EDIT ? "grid" : "flex",
            flex: 1,
            width: "100%",
            height: "100%",
            overflow: "hidden",
            gap: 2,
            gridTemplateColumns:
              mode === RenderState.EDIT
                ? { xs: "1fr", md: "minmax(0, 45fr) minmax(0, 55fr)" }
                : undefined,
          }}
        >
          {/** 기계/기구 유해·위험요인 등록 테이블 */}
          <Box
            data-autopilot="register-cause-asset-list"
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              minWidth: 0,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "0.375rem",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
            }}
          >
            <NewTableTemplate
              ref={registerCauseTableRef}
              columns={getColums()}
              data={filteredRegisterCauseData}
              topToolbarContent={customRegisterCauseTableTopToolbar}
              // renderRowActionContent={customRenderRowActionContent}
              onSelectedRowsChange={(rowIds) => {
                const ids = getRegisterCauseSelectedRows().map(
                  (id) => id.original.registerAssetData.register_asset_id
                );
                setEditItemSelect({ registerAssetIds: ids });
              }}
              enabledSelectionRow={!isReadOnly && mode !== RenderState.VIEW}
              enableCustomGlobalFilter={true}
              enableRowVirtualization={false}
              // enableEditing={mode === RenderState.ADMIN}
              enableEditing={false}
              editDisplayMode={"row"}
              renderRowActionCustomOptions={({ values, row, table }) => {
                const normalizedValues = normalizeToArray(values);
                if (!isReadOnly && deleteMode) openDeleteModal(normalizedValues);
              }}
              tableMeta={{
                recentRegisterCause,
                registerCauseDeleteMode: !isReadOnly && deleteMode,
              }}
              // templateSX ={{
              //   border: `1px solid ${theme.palette.divider}`,
              //   borderRadius: "0.375rem",
              //   boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
              // }}
            />
          </Box>

          {/** 위험발생상황 및 결과 테이블 */}
          <Slide
            direction="left"
            in={!isReadOnly && mode === RenderState.EDIT}
            mountOnEnter
            unmountOnExit
          >
            <Box
              data-autopilot="register-cause-situation-list"
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minWidth: 0,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "0.375rem",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              {selectedEditItems.registerAssetIds.length > 0 ? (
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <NewTableTemplate
                  ref={situationTableRef}
                  columns={situationColumns}
                  data={filteredSituationData}
                  //onSelectedRowsChange={handleSetMachineRow}
                  topToolbarContent={customSituationTableTopToolbar}
                  bottomToolbarContent={customSituationTableBottomToolbar}
                  renderRowActionContent={customEditSituationRowAction}
                  //enableEditing={false}
                  //enableCustomTopToolbarActions={false}
                  enableCustomGlobalFilter={true}
                  //enableBottomToolbar={false}
                  //templateSX={{ height: '35vh' }}
                />
              </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                    bgcolor: "#fff",
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      maxWidth: 420,
                      p: 3,
                      textAlign: "center",
                      border: "1px dashed #cbd5e1",
                      borderRadius: 2,
                      bgcolor: "#f8fafc",
                    }}
                  >
                    <Chip
                      size="small"
                      label="1st"
                      color="primary"
                      sx={{ mb: 1, fontWeight: 900 }}
                    />
                    <Typography variant="body2" sx={{ mt: 0.75, color: "#64748b", lineHeight: 1.6 }}>
                      선택한 대상에 연결할 수 있는 유해·위험요인 목록이 다음 단계에 표시됩니다.
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          </Slide>
        </Box>
        {/* 위험발생상황 및 결과 행(row) 추가 모달 */}
        <CreateSituationRowModal
          isOpen={showAddSituationModal.isOpen}
          originalRowData={[...getData.data]}
          categorys={getSituationFilterListData.data}
          onConfirm={addSituationData}
          onCancel={closeAddSituationModal}
        />
        {/* 위험발생상황 및 결과 행(row) 수정 모달 */}
        <EditSituationRowModal
          isOpen={showEditSituationModal.isOpen}
          originalRowData={showEditSituationModal.rowData}
          categorys={getSituationFilterListData.data}
          onConfirm={updateSituationData}
          onCancel={closeEditSituationModal}
        />
        {/* 행(row) 사본 추가 모달 */}
        <CreateSituationCopyRowModal
          isOpen={showSituationCopyModal.isOpen}
          originalRowData={showSituationCopyModal.rowData}
          categorys={getSituationFilterListData.data}
          onConfirm={addSituationData}
          onCancel={closeSituationCopyModal}
        />
        {/* 행(row) 수정 모달 */}
        <EditRowModalAdmin
          isOpen={editModal.isOpen}
          originalRowData={editModal.rowData}
          onConfirm={updateEditData}
          onCancel={closeEditModal}
        />
        {/* 논리 삭제 메시지 */}
        <ConfirmDialog
          open={deleteModal.isOpen}
          title="위험요인을 비활성화하시겠어요?"
          message="기존 평가 이력은 유지되며 신규 평가 대상에서 제외됩니다."
          onConfirm={softDeleteRegisterCauseData}
          onCancel={closeDeleteModal}
        >
          <TextField
            label={"비활성화 사유"}
            value={deleteModal.deleteReason || ""}
            fullWidth
            onChange={(e) => {
              setDeleteModal({
                ...deleteModal,
                deleteReason: e.target.value,
              });
            }}
            sx={{
              mt: 4,
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.background.paper,
              },
            }}
          />
        </ConfirmDialog>
        {/** 물리 삭제 메시지 */}
        <ConfirmDialog
          open={deleteRecordModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={deleteRegisterCauseData}
          onCancel={closeDeleteRecordModal}
        />
        {/* 데이터 알람 */}
        <SnackbarAlert
          open={validationAlert.isOpen}
          onClose={closeValidationAlert}
          message={validationAlert.message}
          severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
          //anchorOrigin = {{vertical: "top", horizontal: "center"}}
        />
        {/* 기타 메시지 박스 알람 */}
        <ConfirmDialogSingle
          open={messageAlarm.isOpen}
          title={messageAlarm.title}
          message={messageAlarm.message}
          onCancel={closeMessageAlarm}
        />
        <AutoPilotGuide
          open={autopilotOpen}
          steps={REGISTER_CAUSE_AUTOPILOT_STEPS}
          stepIndex={autopilotStepIndex}
          onStepChange={setAutopilotStepIndex}
          onClose={() => setAutopilotOpen(false)}
        />
      </TableContainer>
    );
  }

  // RegisterCause 렌더링
  return (
    <Box
      sx={{
        bgcolor: "var(--mui-palette-background-paper)",
        height: embedded ? "auto" : "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 브레드크럼 + 타이틀 */}
      {!embedded && <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />}
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}

////////////////////////////
// 위험발생상황 및 결과 관련 함수
////////////////////////////
// 위험발생상황 및 결과 테이블의 행(row) 생성 모달 컴포넌트
const CreateSituationRowModal = ({
  isOpen,
  originalRowData,
  categorys,
  onConfirm,
  onCancel,
}) => {
  // const theme = useTheme();
  const { group_code, project_code } = useParams();
  const api = useProjectApi(group_code, project_code);

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: "",
    severity: "",
  });
  const openValidationAlert = (message, severity = "error") => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === "clickaway") return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: "",
      severity: "",
    });
  };

  // 추가 행 데이터 관리
  const [addData, setAddData] = useState([]);
  const clearAddData = () => setAddData([]);

  // 행 추가 완료
  const handleApplyAdd = async () => {
    if (checkEmptyData(addData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    }
    if (await checkValidDBData(addData)) {
      openValidationAlert(message.retryAlreadyNotAdded);
      return;
    }

    onConfirm(addData);
    clearAddData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data) ? [...data] : [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if (tempData.length === 0) {
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      (item) =>
        !item.danger_category || !item.danger_cause || !item.danger_description
    );
    return hasValidData;
  };
  // 데이터 유효성 체크
  const checkValidDBData = async (data) => {
    const params = {
      params: {
        danger_category: data.danger_category,
        danger_cause: data.danger_cause,
        danger_description: data.danger_description,
      },
    };
    const result = await api.get("/getSafeSituationCount", params);
    //console.log(result.data.count);
    return result.data.count > 0;
  };

  // 위험분류 목록
  const dangerCategoryList = Array.from(
    new Set(categorys.map((item) => item.danger_category_filter))
  );
  // 위험원인 목록
  const dangerCauseList = categorys
    .filter((item) => {
      return item.danger_category_filter === addData?.danger_category;
    })
    .flatMap((item) => {
      return item.danger_cause_filters.map((filter) => filter.danger_cause);
    });

  // CreateRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={handleApplyAdd}
      handleCancel={() => {
        clearAddData();
        onCancel();
      }}
      title="추가하기"
      cancelText="취소"
      confirmText="추가"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          gap: 3,
          py: 2,
        }}
      >
        {/* 위험분류 */}
        <FormControl fullWidth>
          <InputLabel>{"위험분류"}</InputLabel>
          <Select
            label={"위험분류"}
            value={addData?.danger_category || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setAddData({
                ...addData,
                danger_category: e.target.value,
                danger_cause: "", // 위험원인 초기화
              });
            }}
          >
            {dangerCategoryList.map((category, index) => (
              <CustomMenuItem key={index} value={category}>
                {category}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>
        {/* 위험원인 */}
        <FormControl fullWidth>
          <InputLabel>{"위험원인"}</InputLabel>
          <Select
            label={"위험원인"}
            value={addData?.danger_cause || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setAddData({
                ...addData,
                danger_cause: e.target.value,
              });
            }}
          >
            {dangerCauseList.map((cause, index) => (
              <CustomMenuItem key={index} value={cause}>
                {cause}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ borderColor: "grey.400", marginX: 2, mt: 2, mb: 2 }} />

        {/* 위험요인 발생상황 및 결과 */}
        <TextField
          label={"유해·위험요인 발생상황 및 결과"}
          value={addData?.danger_description || ""}
          fullWidth
          onChange={(e) => {
            setAddData({
              ...addData,
              danger_description: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{ width: "80%" }}
      />
    </ModalTemplate>
  );
};
// 위험발생상황 및 결과 테이블의 행(row) 수정 모달 컴포넌트
const EditSituationRowModal = ({
  isOpen,
  originalRowData,
  categorys,
  onConfirm,
  onCancel,
}) => {
  const { group_code, project_code } = useParams();
  const api = useProjectApi(group_code, project_code);

  // 유효성 검사 알람 상태 관리(빈칸)
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: "",
    severity: "",
  });
  const openValidationAlert = (message, severity = "error") => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === "clickaway") return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: "",
      severity: "",
    });
  };

  // 수정할 행(row)의 데이터 저장
  const [editData, setEditData] = useState([]);
  const clearEditData = () => setEditData([]);
  useEffect(() => {
    setEditData(originalRowData);
  }, [originalRowData]);

  // 행 수정
  const handleEditRowConfirm = async () => {
    if (checkEmptyData(editData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    }
    if (await checkValidDBData(editData)) {
      openValidationAlert(message.retryAlreadyNotChanged);
      return;
    }

    onConfirm(editData);
    clearEditData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data) ? [...data] : [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if (tempData.length === 0) {
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      (item) =>
        !item.danger_category || !item.danger_cause || !item.danger_description
    );
    return hasValidData;
  };
  // 데이터 유효성 체크
  const checkValidDBData = async (data) => {
    const params = {
      params: {
        danger_category: data.danger_category,
        danger_cause: data.danger_cause,
        danger_description: data.danger_description,
      },
    };
    const result = await api.get("/getSafeSituationCount", params);
    //console.log(result.data.count);
    return result.data.count > 0;
  };

  // 위험분류 목록
  const dangerCategoryList = Array.from(
    new Set(categorys.map((item) => item.danger_category_filter))
  );
  // 위험원인 목록
  const dangerCauseList = categorys
    .filter((item) => {
      return item.danger_category_filter === editData?.danger_category;
    })
    .flatMap((item) => {
      return item.danger_cause_filters.map((filter) => filter.danger_cause);
    });

  // EditRowModalContent 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={handleEditRowConfirm}
      handleCancel={() => {
        clearEditData();
        onCancel();
      }}
      title="수정하기"
      cancelText="취소"
      confirmText="수정"
      contentSX={{ gap: "0px" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          gap: 3,
          py: 2,
        }}
      >
        {/* 위험분류 */}
        <FormControl fullWidth>
          <InputLabel>{"위험분류"}</InputLabel>
          <Select
            label={"위험분류"}
            value={editData?.danger_category || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setEditData({
                ...editData,
                danger_category: e.target.value,
                danger_cause: "", // 위험원인 초기화
              });
            }}
          >
            {dangerCategoryList.map((category, index) => (
              <CustomMenuItem key={index} value={category}>
                {category}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>
        {/* <TextField
          label={'위험분류'}
          value={editedRow?.danger_category || ""}
          type="text"
          fullWidth
          margin="normal"
          onChange={(e) => {
            seteditedRow({
              ...editedRow,
              danger_category: e.target.value,
            });
          }}
        /> */}
        {/* 위험원인 */}
        <FormControl fullWidth>
          <InputLabel>{"위험원인"}</InputLabel>
          <Select
            label={"위험원인"}
            value={editData?.danger_cause || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setEditData({
                ...editData,
                danger_cause: e.target.value,
              });
            }}
          >
            {dangerCauseList.map((cause, index) => (
              <CustomMenuItem key={index} value={cause}>
                {cause}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>
        {/* <TextField
          label={'유해·위험요인'}
          value={editedRow?.danger_cause || ""}
          type="text"
          fullWidth
          margin="normal"
          onChange={(e) => {
            seteditedRow({
              ...editedRow,
              danger_cause: e.target.value,
            });
          }}
        /> */}

        <Divider sx={{ borderColor: "grey.400", marginX: 2, mt: 2, mb: 2 }} />

        {/* 위험요인 발생상황 및 결과 */}
        <TextField
          label={"유해·위험요인 발생상황 및 결과"}
          value={editData?.danger_description || ""}
          type="text"
          fullWidth
          //margin="normal"
          onChange={(e) => {
            setEditData({
              ...editData,
              danger_description: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{ width: "80%" }}
      />
    </ModalTemplate>
  );
};
//위험발생상황 및 결과 테이블의 행(row) 사본 추가 모달 컴포넌트
const CreateSituationCopyRowModal = ({
  isOpen,
  originalRowData,
  categorys,
  onConfirm,
  onCancel,
}) => {
  const { group_code, project_code } = useParams();
  const api = useProjectApi(group_code, project_code);

  // 유효성 검사 알람 상태 관리(빈칸)
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: "",
    severity: "",
  });
  const openValidationAlert = (message, severity = "error") => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === "clickaway") return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: "",
      severity: "",
    });
  };

  // 추가할 행(row)의 데이터 저장
  const [copyData, setCopyData] = useState([]);
  const clearCopyData = () => setCopyData([]);
  useEffect(() => {
    setCopyData(originalRowData);
  }, [originalRowData]);

  // 행 사본 추가
  const handleCopyRowConfirm = async () => {
    if (checkEmptyData(copyData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    }
    if (await checkValidDBData(copyData)) {
      openValidationAlert(message.retryAlreadyNotChanged);
      return;
    }

    onConfirm(copyData);
    clearCopyData();
  };

  // 데이터 유효성 체크
  const checkValidDBData = async (data) => {
    const params = {
      params: {
        danger_category: data.danger_category,
        danger_cause: data.danger_cause,
        danger_description: data.danger_description,
      },
    };
    const result = await api.get("/getSafeSituationCount", params);
    //console.log(result.data.count);
    return result.data.count > 0;
  };
  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data) ? [...data] : [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if (tempData.length === 0) {
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      (item) =>
        !item.danger_category || !item.danger_cause || !item.danger_description
    );
    return hasValidData;
  };

  // 위험분류 목록
  const dangerCategoryList = Array.from(
    new Set(categorys.map((item) => item.danger_category_filter))
  );
  // 위험원인 목록
  const dangerCauseList = categorys
    .filter((item) => {
      return item.danger_category_filter === copyData?.danger_category;
    })
    .flatMap((item) => {
      return item.danger_cause_filters.map((filter) => filter.danger_cause);
    });

  // CopyRowModalContent 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={handleCopyRowConfirm}
      handleCancel={() => {
        clearCopyData();
        onCancel();
      }}
      title="사본 추가"
      cancelText="취소"
      confirmText="추가"
      contentSX={{ gap: "0px" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          gap: 3,
          py: 2,
        }}
      >
        {/* 위험분류 */}
        <FormControl fullWidth>
          <InputLabel>{"위험분류"}</InputLabel>
          <Select
            label={"위험분류"}
            value={copyData?.danger_category || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setCopyData({
                ...copyData,
                danger_category: e.target.value,
                danger_cause: "", // 위험원인 초기화
              });
            }}
          >
            {dangerCategoryList.map((category, index) => (
              <CustomMenuItem key={index} value={category}>
                {category}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>
        {/* 위험원인 */}
        <FormControl fullWidth>
          <InputLabel>{"위험원인"}</InputLabel>
          <Select
            label={"위험원인"}
            value={copyData?.danger_cause || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setCopyData({
                ...copyData,
                danger_cause: e.target.value,
              });
            }}
          >
            {dangerCauseList.map((cause, index) => (
              <CustomMenuItem key={index} value={cause}>
                {cause}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ borderColor: "grey.400", marginX: 2, mt: 2, mb: 2 }} />

        {/* 위험요인 발생상황 및 결과 */}
        <TextField
          label={"유해·위험요인 발생상황 및 결과"}
          value={copyData?.danger_description || ""}
          type="text"
          fullWidth
          //margin="normal"
          onChange={(e) => {
            setCopyData({
              ...copyData,
              danger_description: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{ width: "80%" }}
      />
    </ModalTemplate>
  );
};

/////////
// 테이블의 행(row) 수정 모달 - 관리자 모드
const EditRowModalAdmin = ({
  isOpen,
  originalRowData,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: "",
    severity: "",
  });
  const openValidationAlert = (message, severity = "error") => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === "clickaway") return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: "",
      severity: "",
    });
  };

  // 수정 행 데이터 관리
  const [editData, setEditData] = useState([]);
  const clearEditData = () => setEditData([]);
  useEffect(() => {
    if (!originalRowData || originalRowData.length === 0) {
      return;
    }

    const tempData = {
      ...originalRowData,
      situationData: originalRowData.situationData.map((s) => ({
        ...s,
        // 여기서 원하는 값으로 변경
        register_cause_create_date: formatDateToDayjs(
          s.register_cause_create_date
        ),
        register_cause_delete_date: formatDateToDayjs(
          s.register_cause_delete_date
        ),
      })),
    };

    setEditData(tempData);
  }, [originalRowData]);

  // 행 수정 완료 액션
  const editAction = () => {
    if (checkEmptyData(editData)) {
      clearEditData();
      onCancel();
      return;
    }
    if (validateData(editData)) {
      openValidationAlert(message.notAllowedEmptyRegisterDate);
      return;
    }

    //console.log("수정된 데이터:", editData);

    // 데이터 포맷 변경
    const data = {
      ...editData,
      // 변경할 내용
      situationData: editData.situationData.map((s) => ({
        ...s,
        register_cause_create_date: formatDateWithDayjs(
          s.register_cause_create_date
        ),
        register_cause_delete_date: formatDateWithDayjs(
          s.register_cause_delete_date
        ),
        register_cause_status: s.register_cause_delete_date ? 1 : 0, // 삭제일자가 있으면 상태 1(삭제), 없으면 0(정상)
      })),
    };

    onConfirm(data);
    clearEditData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    if (data.situationData.length === 0) {
      return true;
    }
    return false;
  };
  // 데이터 유효성 체크
  const validateData = (data) => {
    const tempData = data.situationData;
    for (let i = 0; i < tempData.length; i++) {
      const item = tempData[i];
      if (!item.register_cause_create_date) return true;
    }
    return false;
  };

  // EditRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={editAction}
      handleCancel={() => {
        clearEditData();
        onCancel();
      }}
      title="수정하기"
      cancelText="취소"
      confirmText="수정"
      containerSX={{
        width: "auto",
        minWidth: "10%",
        maxWidth: "90%",
        minHeight: "10%",
        maxHeight: "90%",
      }}
      contentSX={{ flexDirection: "column", gap: 2 }}
    >
      <Typography
        sx={{
          fontWeight: FONT_PROPS.MEDIUM,
          fontSize: 16,
          color: theme.palette.text,
        }}
      >
        설비 / 연결 공정:{" "}
        {editData.registerAssetData?.safe_machine_name ||
          editData.registerAssetData?.machine_name}
        {editData.registerAssetData?.process_name
          ? ` / ${editData.registerAssetData.process_name}`
          : ""}
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          flexWrap: "wrap", // 넘치면 다음 줄로
          overflow: "hidden", // 넘치는 부분 숨기기
          overflowY: "auto", // 세로 스크롤
          gap: 1,
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "0.375rem",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        {editData.situationData?.map((s, index) => {
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "auto",
                height: "auto",
                p: 1,
                gap: 3,
              }}
            >
              <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 14 }}>
                {s.machine_name}
              </Typography>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="ko"
              >
                <DatePicker
                  label={"등록일자"}
                  value={
                    editData?.situationData[index]
                      ?.register_cause_create_date || null
                  }
                  onChange={(e) => {
                    const updatedSituationData = [...editData.situationData];
                    updatedSituationData[index] = {
                      ...updatedSituationData[index],
                      register_cause_create_date: e,
                    };
                    setEditData({
                      ...editData,
                      situationData: updatedSituationData,
                    });
                  }}
                  slotProps={{
                    field: { clearable: true },
                    textField: {
                      // fullWidth: true,
                      // sx: {
                      //   marginTop: '16px',
                      //   marginBottom: '8px',
                      // },
                    },
                  }}
                />
                <DatePicker
                  label={"삭제일자"}
                  value={
                    editData?.situationData[index]
                      ?.register_cause_delete_date || null
                  }
                  onChange={(e) => {
                    const updatedSituationData = [...editData.situationData];
                    updatedSituationData[index] = {
                      ...updatedSituationData[index],
                      register_cause_delete_date: e,
                      delete_reason: e
                        ? updatedSituationData[index].delete_reason
                        : null, // 삭제일자가 비워지면 삭제사유도 초기화
                    };
                    setEditData({
                      ...editData,
                      situationData: updatedSituationData,
                    });
                  }}
                  slotProps={{
                    field: { clearable: true },
                    textField: {
                      // fullWidth: true,
                      // sx: {
                      //   marginTop: '16px',
                      //   marginBottom: '8px',
                      // },
                    },
                  }}
                />
              </LocalizationProvider>
              <TextField
                label={"삭제사유"}
                value={editData?.situationData[index]?.delete_reason || ""}
                fullWidth
                onChange={(e) => {
                  const updatedSituationData = [...editData.situationData];
                  updatedSituationData[index] = {
                    ...updatedSituationData[index],
                    delete_reason: e.target.value,
                  };
                  setEditData({
                    ...editData,
                    situationData: updatedSituationData,
                  });
                }}
              />
            </Box>
          );
        })}
        {editData.situationData?.length === 0 && (
          <Typography
            sx={{
              fontWeight: FONT_PROPS.MEDIUM,
              fontSize: 14,
              color: theme.palette.text.secondary,
            }}
          >
            연결된 유해·위험요인이 없습니다.
          </Typography>
        )}
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{ width: "80%" }}
      />
    </ModalTemplate>
  );
};
